import { init as initApm } from '@elastic/apm-rum';
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { persistStore } from 'redux-persist';

import { version } from '../package.json';
import AppCrashFallback from './components/AppCrashFallback/AppCrashFallback';
import Authenticator from './components/Authenticator/Authenticator';
import Switcharoo from './components/Switcharoo/Switcharoo';
import { ConsumerLoanTargetPath } from './domain/types';
import { Environment, toEnvironment } from './domain/types/Environment';
import { MockServiceWorker } from './mocks/mockServiceWorker';
import { CalculatorPage } from './pages/CalculatorPage/CalculatorPage';
import CallbackPage from './pages/CallbackPage/CallbackPage';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import InitCoApplicantPage from './pages/InitCoApplicantPage/InitCoApplicantPage';
import CoApplicantInitDraftPage from './pages/InitDraft/CoApplicantDraftInitPage';
import InitDraftPage from './pages/InitDraft/InitDraftPage';
import InitPage from './pages/InitPage/InitPage';
import LogoutPage from './pages/LogoutPage/LogoutPage';
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import RootPage from './pages/RootPage/RootPage';
import { store } from './store';
import CoApplicantDraftPages from './wizards/DraftWizard/CoApplicantDraftWizard';
import DraftPages from './wizards/DraftWizard/DraftWizard';
import OngoingApplicationPageDK from './wizards/OngoingApplicationWizard/OngoingApplicationWizardDK';
import OngoingApplicationPageNO from './wizards/OngoingApplicationWizard/OngoingApplicationWizardNO';
import OngoingApplicationPageSE from './wizards/OngoingApplicationWizard/OngoingApplicationWizardSE';
import ApplicationPageDK from './wizards/SubmitApplicationWizard/SubmitApplicationWizardDK';
import ApplicationPageNO from './wizards/SubmitApplicationWizard/SubmitApplicationWizardNO';
import ApplicationPageSE from './wizards/SubmitApplicationWizard/SubmitApplicationWizardSE';
import CoApplicantPagesDK from './wizards/SubmitCoApplicantWizard/SubmitCoApplicantWizardDK';
import CoApplicantPagesNO from './wizards/SubmitCoApplicantWizard/SubmitCoApplicantWizardNO';

const currentEnv = toEnvironment(import.meta.env.VITE_ENVIRONMENT);

if (currentEnv === Environment.Unknown) {
    console.warn(`[App Init] Unknown environment: "${import.meta.env.VITE_ENVIRONMENT}". App will fail to load.`);
}

if (import.meta.env.VITE_APM_DSN && currentEnv === Environment.Production) {
    initApm({
        serviceName: import.meta.env.VITE_APPLICATION,
        serverUrl: import.meta.env.VITE_APM_DSN,
        serviceVersion: version,
        environment: import.meta.env.VITE_APM_ENVIRONMENT,
    });
}

const tracesSampleRate = import.meta.env.VITE_SENTRY_SAMPLE_RATE ? import.meta.env.VITE_SENTRY_SAMPLE_RATE : 0;

if (import.meta.env.VITE_SENTRY_DSN && currentEnv === Environment.Production) {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate,
        environment: import.meta.env.VITE_ENVIRONMENT,
        release: `${import.meta.env.VITE_APPLICATION}@${version}`,
        sendDefaultPii: false,
        beforeSend(event) {
            if (event.request) {
                delete event.request.data;
                delete event.request.cookies;
                if (event.request.headers) {
                    const denylist = new Set(['authorization', 'apikey', 'cookie', 'set-cookie']);
                    for (const key of Object.keys(event.request.headers)) {
                        if (denylist.has(key.toLowerCase())) {
                            delete event.request.headers[key];
                        }
                    }
                }
            }
            return event;
        },
    });
}

const sentryCreateBrowserRouter =
    currentEnv === Environment.Production ? Sentry.wrapCreateBrowserRouterV6(createBrowserRouter) : createBrowserRouter;

const router = sentryCreateBrowserRouter([
    {
        element: <RootPage />,
        errorElement: <AppCrashFallback />,
        children: [
            { path: '/*', element: <NotFoundPage /> },
            { path: '/', element: <CalculatorPage /> },
            { path: ConsumerLoanTargetPath.calculator, element: <CalculatorPage /> },
            { path: ConsumerLoanTargetPath.init, element: <InitPage /> },
            {
                path: ConsumerLoanTargetPath.application,
                element: (
                    <Switcharoo
                        formSE={<ApplicationPageSE />}
                        formNO={<ApplicationPageNO />}
                        formDK={<ApplicationPageDK />}
                    />
                ),
            },
            { path: ConsumerLoanTargetPath.coapplicantInit, element: <InitCoApplicantPage /> },
            {
                path: ConsumerLoanTargetPath.coapplicant,
                element: (
                    <Switcharoo
                        formSE={<CoApplicantPagesNO />}
                        formNO={<CoApplicantPagesNO />}
                        formDK={<CoApplicantPagesDK />}
                    />
                ),
            },
            {
                path: ConsumerLoanTargetPath.ongoing,
                element: (
                    <Authenticator autoLogin={false}>
                        <Switcharoo
                            formSE={<OngoingApplicationPageSE />}
                            formNO={<OngoingApplicationPageNO />}
                            formDK={<OngoingApplicationPageDK />}
                        />
                    </Authenticator>
                ),
            },
            {
                path: ConsumerLoanTargetPath.callback,
                element: (
                    <Authenticator autoLogin={true}>
                        <CallbackPage />
                    </Authenticator>
                ),
            },
            {
                path: ConsumerLoanTargetPath.logout,
                element: <LogoutPage />,
            },
            {
                path: ConsumerLoanTargetPath.error,
                element: <ErrorPage />,
            },
            {
                path: ConsumerLoanTargetPath.draftInit,
                element: <InitDraftPage />,
            },
            {
                path: ConsumerLoanTargetPath.draft,
                element: <DraftPages />,
            },
            {
                path: ConsumerLoanTargetPath.coapplicantDraftInit,
                element: <CoApplicantInitDraftPage />,
            },
            {
                path: ConsumerLoanTargetPath.coapplicantDraft,
                element: <CoApplicantDraftPages />,
            },
        ],
    },
]);

export const persistedStore = persistStore(store);

const bootstrapApplication = async () => {
    await MockServiceWorker.initialize();
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
        <Provider store={store}>
            <RouterProvider router={router} />
        </Provider>,
    );
};

bootstrapApplication();
